import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import { getAllBlogs, deleteBlog, updateBlog } from "../apis/Blog";
import { toast } from "react-toastify";

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [currentBlog, setCurrentBlog] = useState({
    id: null,
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    status: "Draft",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState("All");
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (isMounted) {
      fetchBlogs();
    }
  }, [page, rowsPerPage, filterStatus, isMounted]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await getAllBlogs(page, rowsPerPage, filterStatus);
      if (isMounted) {
        setBlogs(response.data);
        setTotalCount(response.meta.totalCount);
      }
    } catch (error) {
      toast.error("Failed to fetch blogs");
      console.error("Error fetching blogs:", error);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setCurrentBlog({
      id: null,
      title: "",
      description: "",
      date: new Date().toISOString().split("T")[0],
      status: "Draft",
    });
    setIsEditing(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentBlog({
      id: null,
      title: "",
      description: "",
      date: new Date().toISOString().split("T")[0],
      status: "Draft",
    });
  };

  const handleEdit = (blog) => {
    setCurrentBlog(blog);
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleDeleteClick = (blog) => {
    setBlogToDelete(blog);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (blogToDelete) {
      try {
        await deleteBlog(blogToDelete.id);
        toast.success("Blog deleted successfully");
        // Refresh the blogs list after successful deletion
        fetchBlogs();
      } catch (error) {
        toast.error("Failed to delete blog");
        console.error("Error deleting blog:", error);
      }
      setOpenDeleteDialog(false);
      setBlogToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setBlogToDelete(null);
  };

  const handleToggleStatus = async (blog) => {
    try {
      const newStatus = blog.status === "Published" ? "Draft" : "Published";
      await updateBlog(blog.id, { status: newStatus });

      // Update local state after successful API call
      setBlogs(
        blogs.map((b) =>
          b.id === blog.id
            ? {
                ...b,
                status: newStatus,
              }
            : b
        )
      );
      toast.success(`Blog ${newStatus.toLowerCase()} successfully`);
    } catch (error) {
      toast.error("Failed to update blog status");
      console.error("Error updating blog status:", error);
    }
  };

  const handleSave = (publish = false) => {
    if (isEditing) {
      setBlogs(
        blogs.map((blog) =>
          blog.id === currentBlog.id
            ? {
                ...currentBlog,
                status: publish ? "Published" : currentBlog.status,
              }
            : blog
        )
      );
    } else {
      const newBlog = {
        ...currentBlog,
        id: Math.max(...blogs.map((b) => b.id), 0) + 1,
        status: publish ? "Published" : "Draft",
      };
      setBlogs([...blogs, newBlog]);
    }
    handleCloseDialog();
  };

  if (loading && blogs.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" component="h1">
          Blog Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/blogs/new")}
        >
          Add Blog
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Published">Published</MenuItem>
              <MenuItem value="Draft">Draft</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blogs.map((blog) => (
                <TableRow key={blog.id}>
                  <TableCell>{blog.title}</TableCell>
                  <TableCell>
                    {new Date(blog.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={blog.status === "Published"}
                          onChange={() => handleToggleStatus(blog)}
                          color="primary"
                        />
                      }
                      label={
                        <Chip
                          label={blog.status}
                          color={
                            blog.status === "Published" ? "success" : "warning"
                          }
                          size="small"
                        />
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => navigate(`/blogs/${blog.id}`)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => navigate(`/blogs/edit/${blog.id}`)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDeleteClick(blog)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isEditing ? "Edit Blog" : "Add Blog"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              value={currentBlog.title}
              onChange={(e) =>
                setCurrentBlog({ ...currentBlog, title: e.target.value })
              }
            />
            <TextField
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={currentBlog.date}
              onChange={(e) =>
                setCurrentBlog({ ...currentBlog, date: e.target.value })
              }
            />
            <Box sx={{ height: 300, mb: 3 }}>
              <ReactQuill
                theme="snow"
                value={currentBlog.description}
                onChange={(value) =>
                  setCurrentBlog({ ...currentBlog, description: value })
                }
                style={{ height: "250px" }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={() => handleSave(false)} color="primary">
            Save
          </Button>
          <Button
            onClick={() => handleSave(true)}
            color="primary"
            variant="contained"
          >
            Save & Publish
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the blog "{blogToDelete?.title}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Blog;
